/**
 * ☯️ Soul Key Panel — Battle Arena consumable for free hero-class swaps.
 *
 * Acquisition (4 alternative paths) + the Guild Lever (1% drop):
 *   • 100 🐦‍🔥 Phoenix Tokens
 *   • 5 Ⓞ Omni Dollars
 *   • 250 🍚 Battle Spirit
 *   • 2,500 ⚔️ Battle Coins
 *
 * Use: consume 1 Soul Key → swap your active hero class to any unlocked
 * class. Preserves level/xp/rebirth. Lore gates (Miko, Corros) still apply.
 */
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { confirmDialog } from "@/components/ui/confirm-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SoulKeyPanel() {
  const { isAuthed } = useAuth();
  const [state, setState] = useState(null);
  const [heroState, setHeroState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSwap, setShowSwap] = useState(false);
  const [pickedClassId, setPickedClassId] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    // 🛡️ Fetch independently — the swap flow needs hero-class/state, but the
    // panel + buy flow only needs soul-key/state. Previously a hero-class
    // failure wiped the entire panel because both were awaited together.
    try {
      const sk = await axios.get(`${API}/soul-key/state`);
      setState(sk.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Soul Key state unavailable — tap retry.");
    }
    try {
      const hc = await axios.get(`${API}/hero-class/state`);
      setHeroState(hc.data);
    } catch (_) { /* swap dropdown will fallback to a static class list */ }
    setLoading(false);
  };

  useEffect(() => {
    if (isAuthed) refresh();
    else setLoading(false);
  }, [isAuthed]);

  const handleBuy = async (currency) => {
    const price = (state?.prices || []).find((p) => p.currency === currency);
    const ok = await confirmDialog({
      title: `Buy a Soul Key?`,
      message: `Cost: ${price?.icon || ""} ${price?.cost?.toLocaleString()} ${price?.label || currency}.
Burn 1 Soul Key to swap your active hero class any time. Level + XP + rebirth count carry over.`,
      confirmLabel: `Buy — ${price?.cost?.toLocaleString()} ${price?.label || currency}`,
      cancelLabel: "Not yet",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await axios.post(`${API}/soul-key/purchase`, { currency });
      toast.success("☯️ Soul Key acquired!");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Purchase failed.");
    } finally {
      setBusy(false);
    }
  };

  const handleSwap = async () => {
    if (!pickedClassId) return;
    setBusy(true);
    // 🛡️ Feb 2026 root-cause fix for "I burned a key but still Warrior".
    // The old flow showed `toast.error("Swap failed.")` whenever anything
    // AFTER the HTTP call threw — including unrelated FE issues like a
    // setState-after-unmount or a toast library hiccup — even when the
    // server had already persisted the swap. Users saw the error, re-
    // clicked, and burned a second key for nothing. The new flow:
    //   • Awaits the server first and inspects its payload.
    //   • Only treats the swap as successful when the response confirms
    //     `current_class_id === pickedClassId`.
    //   • If a post-success toast or refresh throws, that is logged but
    //     NEVER downgrades the success toast to an error.
    //   • Error toasts now surface the full error text (status + detail)
    //     so recurrence is debuggable instead of silent.
    let swapResp = null;
    try {
      const res = await axios.post(
        `${API}/soul-key/swap-hero`,
        { class_id: pickedClassId },
      );
      swapResp = res?.data || null;
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const msg = detail
        || (status ? `Swap failed (HTTP ${status})` : (e?.message || "Swap failed — network error"));
      console.error("[soul-key swap] HTTP error:", status, detail, e);
      toast.error(msg);
      setBusy(false);
      return;
    }
    // ✅ Verify the server actually flipped the class before celebrating.
    const confirmedClass = swapResp?.current_class_id;
    if (confirmedClass !== pickedClassId) {
      console.error("[soul-key swap] mismatch", {
        expected: pickedClassId, got: confirmedClass, payload: swapResp,
      });
      toast.error(
        `Swap did not confirm. Server says you are "${confirmedClass || "unknown"}" after the request — refresh and try again. Your Soul Key may not have been consumed.`,
      );
      setBusy(false);
      return;
    }
    toast.success(`☯️ Soul Key consumed — you are now a ${pickedClassId.replaceAll("_", " ")}!`);
    setShowSwap(false);
    setPickedClassId("");
    // Best-effort refresh. If this throws, log it — don't downgrade the
    // confirmed success into an error toast like the old code did.
    try {
      await refresh();
    } catch (e) {
      console.warn("[soul-key swap] post-swap refresh threw:", e);
    } finally {
      setBusy(false);
    }
  };

  // Build the swap dropdown options: every class in the catalog except the
  // current one. Backend will reject gated classes the user hasn't unlocked.
  // 🛡️ Fallback — if /hero-class/state failed, offer the 4 free base classes
  // so the swap flow is never dead-ended by an unrelated failure.
  const swapOptions = useMemo(() => {
    const currentId = state?.current_class_id;
    const cat = heroState?.classes_catalog;
    if (cat && cat.length) return cat.filter((c) => c.id !== currentId);
    return [
      { id: "warrior",   name: "Warrior",   icon: "⚔️" },
      { id: "mage",      name: "Mage",      icon: "🔮" },
      { id: "archer",    name: "Archer",    icon: "🏹" },
      { id: "ash_rogue", name: "Ash Rogue", icon: "🌑" },
    ].filter((c) => c.id !== currentId);
  }, [heroState, state]);

  if (!isAuthed) return null;

  // 🛡️ Soft fallback — if the state fetch failed outright, render a visible
  // retry card so the panel never silently disappears.
  if (!state) {
    return (
      <section
        className="mb-8 rounded-xl border border-amber-900/40 bg-gradient-to-br from-[#1a0f2e]/80 to-[#0F0F0F]/80 p-5"
        data-testid="soul-key-panel"
      >
        <h2 className="font-display text-2xl text-amber-200 mb-2">☯️ Soul Key</h2>
        <p className="text-xs text-neutral-400 mb-3">
          {loading ? "Loading your Soul Keys..." : (error || "Soul Key state temporarily unavailable.")}
        </p>
        {!loading && (
          <button
            onClick={refresh}
            data-testid="soul-key-retry"
            className="rounded-lg bg-amber-500 text-black px-4 py-2 text-sm font-bold hover:bg-amber-300"
          >
            Retry
          </button>
        )}
      </section>
    );
  }

  return (
    <section
      className="mb-8 rounded-xl border border-amber-900/40 bg-gradient-to-br from-[#1a0f2e]/80 to-[#0F0F0F]/80 p-5"
      data-testid="soul-key-panel"
    >
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="font-display text-2xl text-amber-200">
          ☯️ Soul Key
        </h2>
        <span
          className="text-[11px] uppercase tracking-widest text-neutral-400"
          data-testid="soul-key-count"
        >
          Owned: <span className="text-amber-300 font-bold">{state.soul_keys}</span>
        </span>
      </div>
      <p className="text-xs text-neutral-400 mb-4 leading-relaxed">
        A rare key forged in the embers of forgotten paths. Burn one to swap your
        active hero class — your level, XP, and rebirth count travel with you.
        Locked heroes (Miko, Corros) must still earn their own gates.
      </p>

      {/* Purchase paths */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        {(state.prices || []).map((p) => (
          <button
            key={p.currency}
            disabled={busy || !p.can_afford}
            onClick={() => handleBuy(p.currency)}
            data-testid={`soul-key-buy-${p.currency}`}
            className={
              "rounded-lg border px-3 py-2 text-left transition " +
              (p.can_afford
                ? "border-amber-700/60 bg-amber-950/30 hover:border-amber-400 hover:bg-amber-900/40 text-amber-100"
                : "border-neutral-800 bg-neutral-900/40 text-neutral-500 cursor-not-allowed")
            }
          >
            <div className="text-[11px] uppercase tracking-wider opacity-80">
              {p.icon} {p.label}
            </div>
            <div className="font-mono text-sm mt-0.5">
              {p.cost.toLocaleString()}
            </div>
            <div className="text-[10px] opacity-60 mt-0.5">
              Bal: {p.balance.toLocaleString()}
            </div>
          </button>
        ))}
      </div>

      {/* Use Soul Key */}
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] text-neutral-500">
          🎡 Also drops from the Guild Lever (~{state.lever_drop_chance_pct}%).
        </span>
        <button
          disabled={busy || state.soul_keys < 1}
          onClick={() => setShowSwap(true)}
          data-testid="soul-key-open-swap-btn"
          className={
            "rounded-lg px-4 py-2 text-sm font-bold transition " +
            (state.soul_keys > 0
              ? "bg-amber-500 text-black hover:bg-amber-300"
              : "bg-neutral-800 text-neutral-500 cursor-not-allowed")
          }
        >
          ☯️ Swap Hero ({state.soul_keys})
        </button>
      </div>

      {/* Swap modal */}
      {showSwap && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="soul-key-swap-modal"
        >
          <div className="w-full max-w-md rounded-xl border border-amber-700/60 bg-[#1a0f2e] p-5">
            <h3 className="font-display text-xl text-amber-200 mb-2">
              ☯️ Burn a Soul Key
            </h3>
            <p className="text-xs text-neutral-400 mb-3">
              Choose your next hero. Your level, XP, and rebirth count carry over.
              Current:{" "}
              <span className="text-amber-200 font-bold">
                {(state.current_class_id || "—").replaceAll("_", " ")}
              </span>
            </p>
            <select
              value={pickedClassId}
              onChange={(e) => setPickedClassId(e.target.value)}
              data-testid="soul-key-class-picker"
              className="w-full rounded-lg bg-neutral-900 border border-neutral-700 px-3 py-2 text-sm text-neutral-100 mb-2 focus:border-amber-400 focus:outline-none"
            >
              <option value="">— Pick a class —</option>
              {swapOptions.map((c) => {
                const title = c.picker_title || c.name || c.id;
                const prefix = `${c.icon || ""} ${title}`.trim();
                return (
                  <option key={c.id} value={c.id} disabled={!!c.gated} title={c.gate_reason || ""}>
                    {c.gated ? `🔒 ${prefix} — ${c.gate_reason || "Locked"}` : prefix}
                  </option>
                );
              })}
            </select>
            {(() => {
              const picked = swapOptions.find((c) => c.id === pickedClassId);
              if (picked?.gated) {
                return (
                  <p
                    className="text-[11px] text-amber-300 bg-amber-950/40 border border-amber-800/60 rounded-md px-2 py-1.5 mb-3"
                    data-testid="soul-key-unlock-hint"
                  >
                    🔒 <span className="font-bold">{picked.name}</span> is locked — {picked.gate_reason || "unlock through its own path."}
                  </p>
                );
              }
              return <div className="mb-2" />;
            })()}
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => {
                  setShowSwap(false);
                  setPickedClassId("");
                }}
                disabled={busy}
                data-testid="soul-key-swap-cancel"
                className="rounded-lg border border-neutral-700 px-4 py-2 text-sm text-neutral-300 hover:bg-neutral-800"
              >
                Cancel
              </button>
              <button
                onClick={handleSwap}
                disabled={busy || !pickedClassId || !!swapOptions.find((c) => c.id === pickedClassId)?.gated}
                data-testid="soul-key-swap-confirm"
                className="rounded-lg bg-amber-500 text-black px-4 py-2 text-sm font-bold hover:bg-amber-300 disabled:bg-neutral-800 disabled:text-neutral-500"
              >
                {busy ? "Burning..." : "Burn Key & Swap"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
